import React, { useEffect, useState } from "react"
import {
    Row,
    Col,
    Card,
    CardBody,
    TabContent,
    TabPane,
    Nav,
    NavItem,
    NavLink,
    Button,
    Table,
    Badge,
    Form,
    FormGroup,
    Label,
    Input,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from "reactstrap"
import classnames from "classnames"
import { connect } from "react-redux"
import { Link } from "react-router-dom"
import { setBreadcrumbItems } from "../../store/actions"
import { projetsAPI, ticketsAPI, usersAPI, meetingsAPI } from "../../services/api"
import AlertModal from "../../components/Common/AlertModal"
import withRouter from "components/Common/withRouter"
import { useAuth } from "../../contexts/AuthContext"

const ProjetDetails = (props) => {
    const { id } = props.router.params
    const { user } = useAuth()
    const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager'
    const isClient = user?.role === 'client'

    const [projet, setProjet] = useState(null)
    const [users, setUsers] = useState([])
    const [activeTab, setActiveTab] = useState("1")

    // Modals
    const [docModal, setDocModal] = useState(false)
    const [etapeModal, setEtapeModal] = useState(false)
    const [ticketModal, setTicketModal] = useState(false)

    // Editing State
    const [editingEtape, setEditingEtape] = useState(null)
    const [editingTicket, setEditingTicket] = useState(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSubmittingTicket, setIsSubmittingTicket] = useState(false)
    const [isSubmittingEtape, setIsSubmittingEtape] = useState(false)
    const [isSubmittingDoc, setIsSubmittingDoc] = useState(false)

    const [alertConfig, setAlertConfig] = useState({
        isOpen: false,
        type: 'success',
        message: '',
        title: ''
    })

    const toggleAlert = () => setAlertConfig({ ...alertConfig, isOpen: !alertConfig.isOpen })

    // Forms
    const [docData, setDocData] = useState({ nom: "", type: "autre", fichier: null })
    const [etapeData, setEtapeData] = useState({ nom: "", date_debut: "", date_fin: "", statut: "en_cours", ordre: 1 })
    const [ticketData, setTicketData] = useState({
        titre: "",
        description: "",
        etape_id: "",
        assigned_to: "",
        priorite: "normale",
        deadline: "",
        statut: "en_cours",
        heures_estimees: "",
        heures_reelles: ""
    })

    const docTypes = [
        { key: 'cahier_charges', label: 'Cahier des charges' },
        { key: 'specification_fonctionnelle', label: 'Spécification fonctionnelle' },
        { key: 'contrat_client', label: 'Contrat client' },
        { key: 'autre', label: 'Autres' },
    ]

    useEffect(() => {
        fetchProjet()
        fetchUsers()
    }, [id])

    const fetchProjet = async () => {
        try {
            const res = await projetsAPI.getById(id)
            setProjet(res.data)
            props.setBreadcrumbItems(res.data.nom, [
                { title: "Dashboard", link: "/dashboard" },
                { title: "Projets", link: "/projets" },
                { title: res.data.nom, link: "#" },
            ])
        } catch (error) {
            console.error("Erreur lors du chargement du projet", error)
        }
    }

    const fetchUsers = async () => {
        if (!isAdminOrManager) return; // Un client n'a pas besoin de charger tous les utilisateurs ici
        try {
            const res = await usersAPI.getAll()
            setUsers(res.data)
        } catch (error) {
            console.error("Erreur users", error)
        }
    }

    // Handlers
    const toggleDocModal = () => {
        setDocModal(!docModal)
        if (!docModal) setDocData({ nom: "", type: "autre", fichier: null })
    }

    const toggleEtapeModal = () => {
        setEtapeModal(!etapeModal)
        if (etapeModal) {
            setEditingEtape(null)
            setEtapeData({ nom: "", date_debut: "", date_fin: "", statut: "en_cours", ordre: 1 })
        }
    }

    const toggleTicketModal = () => {
        setTicketModal(!ticketModal)
        if (ticketModal) {
            setEditingTicket(null)
            setTicketData({
                titre: "",
                description: "",
                etape_id: "",
                assigned_to: "",
                priorite: "normale",
                deadline: "",
                statut: "en_cours",
                heures_estimees: "",
                heures_reelles: ""
            })
        }
    }

    const handleEditEtape = (etape) => {
        setEditingEtape(etape)
        setEtapeData({
            nom: etape.nom,
            date_debut: etape.date_debut ? etape.date_debut.substring(0, 10) : "",
            date_fin: etape.date_fin ? etape.date_fin.substring(0, 10) : "",
            statut: etape.statut,
            ordre: etape.ordre || 1
        })
        setEtapeModal(true)
    }

    const handleDeleteEtape = async (etapeId) => {
        if (window.confirm("Supprimer cette étape ?")) {
            try {
                await projetsAPI.deleteEtape(id, etapeId)
                fetchProjet()
            } catch (error) { console.error(error) }
        }
    }

    const handleEditTicket = (ticket) => {
        setEditingTicket(ticket)
        setTicketData({
            titre: ticket.titre,
            description: ticket.description,
            etape_id: ticket.etape_id || "",
            assigned_to: ticket.assigned_to_id || ticket.assigned_to?.id || "",
            priorite: ticket.priorite,
            deadline: ticket.deadline ? ticket.deadline.substring(0, 10) : "",
            statut: ticket.statut,
            heures_estimees: ticket.heures_estimees || "",
            heures_reelles: ticket.heures_reelles || ""
        })
        setTicketModal(true)
    }

    const handleDeleteTicket = async (ticketId) => {
        if (window.confirm("Supprimer ce ticket ?")) {
            try {
                await ticketsAPI.delete(ticketId)
                fetchProjet()
            } catch (error) { console.error(error) }
        }
    }

    const handleDeleteDoc = async (docId) => {
        if (window.confirm("Supprimer ce document ?")) {
            try {
                await projetsAPI.deleteDocument(id, docId)
                fetchProjet()
            } catch (error) { console.error(error) }
        }
    }

    const handleDocSubmit = async (e) => {
        e.preventDefault()
        if (isSubmittingDoc) return
        setIsSubmittingDoc(true)
        const data = new FormData()
        if (docData.fichier) data.append('fichier', docData.fichier)
        data.append('nom', docData.nom || docTypes.find(t => t.key === docData.type).label)
        data.append('type', docData.type)
        try {
            await projetsAPI.uploadDocument(id, data)
            setAlertConfig({
                isOpen: true,
                type: 'success',
                title: 'Chargé',
                message: 'Le document a été uploader avec succès.'
            })
            toggleDocModal()
            fetchProjet()
        } catch (error) {
            console.error(error)
            setAlertConfig({
                isOpen: true,
                type: 'error',
                title: 'Erreur',
                message: "Une erreur est survenue lors de l'upload du document."
            })
        } finally {
            setIsSubmittingDoc(false)
        }
    }

    const handleEtapeSubmit = async (e) => {
        e.preventDefault()
        if (isSubmittingEtape) return
        setIsSubmittingEtape(true)
        try {
            if (editingEtape) {
                await projetsAPI.updateEtape(id, editingEtape.id, etapeData)
                setAlertConfig({
                    isOpen: true,
                    type: 'success',
                    title: 'Mise à jour',
                    message: "L'étape a été mise à jour avec succès."
                })
            } else {
                await projetsAPI.addEtape(id, etapeData)
                setAlertConfig({
                    isOpen: true,
                    type: 'success',
                    title: 'Ajoutée',
                    message: "L'étape a été ajoutée avec succès."
                })
            }
            toggleEtapeModal()
            fetchProjet()
        } catch (error) {
            console.error(error)
            setAlertConfig({
                isOpen: true,
                type: 'error',
                title: 'Erreur',
                message: "Une erreur est survenue lors de l'enregistrement de l'étape."
            })
        } finally {
            setIsSubmittingEtape(false)
        }
    }

    const handleTicketSubmit = async (e) => {
        e.preventDefault()
        if (isSubmittingTicket) return
        setIsSubmittingTicket(true)
        try {
            if (editingTicket) {
                await ticketsAPI.update(editingTicket.id, { ...ticketData, projet_id: id })
                setAlertConfig({
                    isOpen: true,
                    type: 'success',
                    title: 'Mis à jour',
                    message: 'Le ticket a été mis à jour avec succès.'
                })
            } else {
                await ticketsAPI.create({ ...ticketData, projet_id: id })
                setAlertConfig({
                    isOpen: true,
                    type: 'success',
                    title: 'Créé',
                    message: 'Le ticket a été créé avec succès.'
                })
            }
            toggleTicketModal()
            fetchProjet()
        } catch (error) {
            console.error(error)
            setAlertConfig({
                isOpen: true,
                type: 'error',
                title: 'Erreur',
                message: "Une erreur est survenue lors de l'enregistrement du ticket."
            })
        } finally {
            setIsSubmittingTicket(false)
        }
    }

    if (!projet) return <div>Chargement...</div>

    return (
        <React.Fragment>
            <Row>
                <Col lg="12">
                    <Card>
                        <CardBody>
                            <div className="d-flex align-items-center mb-4 border-bottom pb-3">
                                <div className="flex-grow-1">
                                    <h4 className="card-title mb-1">{projet.nom}</h4>
                                    <div className="d-flex gap-2 align-items-center">
                                        <Badge color={projet.type === 'interne' ? 'info' : 'warning'}>
                                            {projet.type === 'interne' ? 'INTERNE (RAKOPS)' : 'EXTERNE'}
                                        </Badge>
                                        <span className="text-muted border-start ps-2">
                                            {projet.type === 'externe' ? `Client : ${projet.client?.nom || '-'}` : 'Projet Maison'}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex-shrink-0 text-end">
                                    <Badge color={projet.statut === 'termine' ? 'success' : (projet.statut === 'en_cours' ? 'primary' : 'secondary')} className="p-2 mb-2 d-inline-block">
                                        {projet.statut.replace('_', ' ').toUpperCase()}
                                    </Badge>
                                    {isAdminOrManager && (
                                        <div>
                                            <Button color="success" size="sm" onClick={() => { setEditingTicket(null); toggleTicketModal(); }}>
                                                <i className="mdi mdi-plus me-1"></i> Nouveau Ticket
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <Nav tabs className="nav-tabs-custom">
                                <NavItem>
                                    <NavLink className={classnames({ active: activeTab === "1" })} onClick={() => setActiveTab("1")}>Vue d'ensemble</NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink className={classnames({ active: activeTab === "2" })} onClick={() => setActiveTab("2")}>Documentation</NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink className={classnames({ active: activeTab === "3" })} onClick={() => setActiveTab("3")}>Planning & Étapes</NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink className={classnames({ active: activeTab === "4" })} onClick={() => setActiveTab("4")}>Tickets ({projet.tickets?.length || 0})</NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink className={classnames({ active: activeTab === "5" })} onClick={() => setActiveTab("5")}>Réunions ({projet.meetings?.length || 0})</NavLink>
                                </NavItem>
                            </Nav>

                            <TabContent activeTab={activeTab} className="p-3">
                                <TabPane tabId="1">
                                    <Row>
                                        <Col md={8}>
                                            <div className="mb-4">
                                                <h5 className="font-size-15 mb-3">Description du projet</h5>
                                                <p className="text-muted">{projet.description || "Aucune description fournie."}</p>
                                            </div>
                                            <div className="mb-4">
                                                <h5 className="font-size-15 mb-3">Sources & Références</h5>
                                                {projet.github_links ? (
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {projet.github_links.split(',').map((link, i) => (
                                                            <a key={i} href={link.trim()} target="_blank" rel="noreferrer" className="btn btn-sm btn-soft-dark border">
                                                                <i className="mdi mdi-github me-1"></i> Github Repo {i > 0 ? i + 1 : ''}
                                                            </a>
                                                        ))}
                                                    </div>
                                                ) : <span className="text-muted small">Aucun lien Github associé.</span>}
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <Card className="bg-light shadow-none">
                                                <CardBody>
                                                    <h5 className="font-size-15 mb-3 border-bottom pb-2">Résumé</h5>
                                                    <table className="table table-nowrap mb-0 table-sm">
                                                        <tbody>
                                                            <tr><th scope="row">Manager :</th><td>{projet.manager?.name || '-'}</td></tr>
                                                            <tr>
                                                                <th scope="row">Équipes :</th>
                                                                <td>
                                                                    {projet.teams && projet.teams.length > 0 ? (
                                                                        <div className="d-flex flex-wrap gap-1">
                                                                            {projet.teams.map(t => (
                                                                                <Badge key={t.id} color="soft-primary" className="bg-soft-primary text-primary border">
                                                                                    {t.nom}
                                                                                </Badge>
                                                                            ))}
                                                                        </div>
                                                                    ) : '-'}
                                                                </td>
                                                            </tr>
                                                            <tr><th scope="row">Début :</th><td>{projet.date_debut ? new Date(projet.date_debut).toLocaleDateString() : '-'}</td></tr>
                                                            <tr><th scope="row">Échéance :</th><td>{projet.date_fin_prevue ? new Date(projet.date_fin_prevue).toLocaleDateString() : '-'}</td></tr>
                                                        </tbody>
                                                    </table>

                                                    <div className="mt-4">
                                                        <div className="d-flex justify-content-between mb-2"><Label className="mb-0">Réalisé</Label><span className="text-success fw-bold">{projet.avancement_realise || 0}%</span></div>
                                                        <div className="progress progress-sm"><div className="progress-bar bg-success" role="progressbar" style={{ width: `${projet.avancement_realise || 0}%` }}></div></div>
                                                    </div>
                                                    <div className="mt-3">
                                                        <div className="d-flex justify-content-between mb-2"><Label className="mb-0">Prévu</Label><span className="text-info fw-bold">{projet.avancement_prevu || 0}%</span></div>
                                                        <div className="progress progress-sm"><div className="progress-bar bg-info" role="progressbar" style={{ width: `${projet.avancement_prevu || 0}%` }}></div></div>
                                                    </div>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    </Row>
                                </TabPane>

                                <TabPane tabId="2">
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <h5 className="font-size-15 mb-0">Documents du Projet</h5>
                                        {isAdminOrManager && (
                                            <Button color="primary" size="sm" onClick={toggleDocModal}><i className="mdi mdi-upload me-1"></i> Uploader</Button>
                                        )}
                                    </div>
                                    <Row>
                                        {docTypes.map(type => {
                                            const docs = projet.documents?.filter(d => d.type === type.key) || [];
                                            return (
                                                <Col md={3} key={type.key}>
                                                    <Card className="border shadow-none">
                                                        <CardBody className="text-center">
                                                            <div className="avatar-md mx-auto mb-3">
                                                                <span className={`avatar-title rounded-circle bg-soft-${docs.length > 0 ? 'success' : 'light'} text-${docs.length > 0 ? 'success' : 'primary'}`}>
                                                                    <i className={`mdi ${type.key === 'contrat_client' ? 'mdi-file-certificate' : 'mdi-file-document-outline'} h2 mb-0`}></i>
                                                                </span>
                                                            </div>
                                                            <h6 className="text-truncate">{type.label}</h6>
                                                            {docs.length > 0 ? (
                                                                <div className="d-flex flex-column gap-1">
                                                                    {docs.map(doc => (
                                                                        <div key={doc.id} className="d-flex gap-1 justify-content-center">
                                                                            <a href={doc.url} target="_blank" rel="noreferrer" className="btn btn-sm btn-light text-success text-truncate flex-grow-1">
                                                                                <i className="mdi mdi-download me-1"></i> {doc.nom}
                                                                            </a>
                                                                            {isAdminOrManager && (
                                                                                <Button color="light" size="sm" className="text-danger" onClick={() => handleDeleteDoc(doc.id)}>
                                                                                    <i className="mdi mdi-delete"></i>
                                                                                </Button>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : <p className="text-muted small mb-0">Non disponible</p>}
                                                        </CardBody>
                                                    </Card>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                </TabPane>

                                <TabPane tabId="3">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5>Étapes et Jalons</h5>
                                        {isAdminOrManager && (
                                            <Button color="primary" size="sm" onClick={() => { setEditingEtape(null); toggleEtapeModal(); }}><i className="mdi mdi-plus me-1"></i> Ajouter une étape</Button>
                                        )}
                                    </div>
                                    <Table responsive hover className="table-centered table-nowrap">
                                        <thead><tr><th>#</th><th>Étape</th><th>Période</th><th>Statut</th><th className="text-end">Actions</th></tr></thead>
                                        <tbody>
                                            {projet.etapes?.map((etape, index) => (
                                                <tr key={etape.id}>
                                                    <td>{index + 1}</td>
                                                    <td className="fw-semibold">{etape.nom}</td>
                                                    <td><small className="text-muted">{etape.date_debut ? new Date(etape.date_debut).toLocaleDateString() : '-'} au {etape.date_fin ? new Date(etape.date_fin).toLocaleDateString() : '-'}</small></td>
                                                    <td><Badge color={etape.statut === 'termine' ? 'success' : 'warning'}>{etape.statut.replace('_', ' ')}</Badge></td>
                                                    <td className="text-end">
                                                        {isAdminOrManager && (
                                                            <>
                                                                <Button color="link" size="sm" className="p-0 me-2" onClick={() => handleEditEtape(etape)}><i className="mdi mdi-pencil text-primary"></i></Button>
                                                                <Button color="link" size="sm" className="p-0" onClick={() => handleDeleteEtape(etape.id)}><i className="mdi mdi-delete text-danger"></i></Button>
                                                            </>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                            {(!projet.etapes || projet.etapes.length === 0) && <tr><td colSpan="5" className="text-center text-muted py-4">Aucune étape définie.</td></tr>}
                                        </tbody>
                                    </Table>
                                </TabPane>

                                <TabPane tabId="4">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5>Tickets liés</h5>
                                        {isAdminOrManager && (
                                            <Button color="primary" size="sm" onClick={() => { setEditingTicket(null); toggleTicketModal(); }}><i className="mdi mdi-plus me-1"></i> Créer un ticket</Button>
                                        )}
                                    </div>
                                    <Table responsive hover className="table-centered table-nowrap mb-0">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Titre</th>
                                                <th>Assigné</th>
                                                <th>Priorité</th>
                                                <th>Mesurabilité</th>
                                                <th>Statut</th>
                                                <th className="text-end">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {projet.tickets?.map(ticket => (
                                                <tr key={ticket.id}>
                                                    <td>#{ticket.id}</td>
                                                    <td className="fw-semibold">
                                                        <Link to={`/tickets/${ticket.id}`}>{ticket.titre}</Link>
                                                    </td>
                                                    <td>{ticket.assigned_to?.name || '-'}</td>
                                                    <td><Badge color={ticket.priorite === 'urgente' ? 'danger' : (ticket.priorite === 'haute' ? 'warning' : 'primary')}>{ticket.priorite.toUpperCase()}</Badge></td>
                                                    <td>
                                                        <div className="small">
                                                            <div><i className="mdi mdi-clock-outline me-1"></i> {ticket.heures_estimees || 0}h est. / {ticket.heures_reelles || 0}h réelles</div>
                                                            <div className="text-muted mt-1"><i className="mdi mdi-calendar-clock me-1"></i> DL: {ticket.deadline ? new Date(ticket.deadline).toLocaleDateString() : '-'}</div>
                                                        </div>
                                                    </td>
                                                    <td><Badge pill className={`bg-${ticket.statut === 'resolu' ? 'success' : 'info'}`}>{ticket.statut.replace('_', ' ')}</Badge></td>
                                                    <td className="text-end">
                                                        {isAdminOrManager && (
                                                            <>
                                                                <Button color="link" size="sm" className="p-0 me-2" onClick={() => handleEditTicket(ticket)}><i className="mdi mdi-pencil text-primary"></i></Button>
                                                                <Button color="link" size="sm" className="p-0" onClick={() => handleDeleteTicket(ticket.id)}><i className="mdi mdi-delete text-danger"></i></Button>
                                                            </>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                            {(!projet.tickets || projet.tickets.length === 0) && <tr><td colSpan="7" className="text-center text-muted py-4">Aucun ticket ouvert.</td></tr>}
                                        </tbody>
                                    </Table>
                                </TabPane>

                                <TabPane tabId="5">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5>Réunions du projet</h5>
                                    </div>
                                    {projet.meetings && projet.meetings.length > 0 ? (
                                        <Table responsive hover className="table-centered table-nowrap mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Titre</th>
                                                    <th>Date & Heure</th>
                                                    <th>Lieu / Lien</th>
                                                    <th>Participants</th>
                                                    <th>Statut</th>

                                                </tr>
                                            </thead>
                                            <tbody>
                                                {projet.meetings.map(meeting => (
                                                    <tr key={meeting.id}>
                                                        <td className="fw-semibold">{meeting.titre}</td>
                                                        <td>
                                                            <i className="mdi mdi-calendar me-1 text-primary"></i>
                                                            {meeting.date_heure ? new Date(meeting.date_heure).toLocaleDateString('fr-FR') : '-'}
                                                            <br />
                                                            <small className="text-muted">
                                                                <i className="mdi mdi-clock-outline me-1"></i>
                                                                {meeting.date_heure ? new Date(meeting.date_heure).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '-'}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            {meeting.lien_visio ? (
                                                                <a href={meeting.lien_visio} target="_blank" rel="noreferrer" className="text-primary">
                                                                    <i className="mdi mdi-video me-1"></i>Visioconférence
                                                                </a>
                                                            ) : (
                                                                <span className="text-muted">
                                                                    <i className="mdi mdi-map-marker me-1"></i>
                                                                    {meeting.lieu || '-'}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <Badge color="light" className="text-dark">
                                                                <i className="mdi mdi-account-multiple me-1"></i>
                                                                {meeting.participants?.length || 0} personne(s)
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <Badge color={meeting.statut === 'termine' ? 'success' : (meeting.statut === 'en_cours' ? 'warning' : 'primary')}>
                                                                {meeting.statut === 'planifie' ? 'PLANIFIÉ' : meeting.statut === 'en_cours' ? 'EN COURS' : 'TERMINÉ'}
                                                            </Badge>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    ) : (
                                        <div className="text-center py-5">
                                            <div className="avatar-md mx-auto mb-3">
                                                <span className="avatar-title rounded-circle bg-soft-primary text-primary">
                                                    <i className="mdi mdi-video h2 mb-0"></i>
                                                </span>
                                            </div>
                                            <h5 className="text-muted">Aucune réunion planifiée</h5>
                                            <p className="text-muted small">Les réunions liées à ce projet apparaîtront ici.</p>
                                        </div>
                                    )}
                                </TabPane>
                            </TabContent>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Modals */}
            <Modal isOpen={docModal} toggle={toggleDocModal}>
                <ModalHeader toggle={toggleDocModal}>Ajouter un document</ModalHeader>
                <Form onSubmit={handleDocSubmit}><ModalBody>
                    <FormGroup><Label>Type</Label><Input type="select" value={docData.type} onChange={(e) => setDocData({ ...docData, type: e.target.value })}>{docTypes.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}</Input></FormGroup>
                    <FormGroup><Label>Nom du document</Label><Input type="text" placeholder="Ex: Cahier des charges v1" value={docData.nom} onChange={(e) => setDocData({ ...docData, nom: e.target.value })} /></FormGroup>
                    <FormGroup><Label>Fichier</Label><Input type="file" onChange={(e) => setDocData({ ...docData, fichier: e.target.files[0] })} required /></FormGroup>
                </ModalBody><ModalFooter><Button color="secondary" onClick={toggleDocModal} disabled={isSubmittingDoc}>Annuler</Button><Button color="primary" type="submit" disabled={isSubmittingDoc}>{isSubmittingDoc ? <><i className="mdi mdi-loading mdi-spin me-1"></i> Upload...</> : "Uploader"}</Button></ModalFooter></Form>
            </Modal>

            <Modal isOpen={etapeModal} toggle={toggleEtapeModal}>
                <ModalHeader toggle={toggleEtapeModal}>{editingEtape ? "Modifier l'étape" : "Ajouter une étape"}</ModalHeader>
                <Form onSubmit={handleEtapeSubmit}><ModalBody>
                    <FormGroup><Label>Nom de l'étape</Label><Input type="text" value={etapeData.nom} onChange={(e) => setEtapeData({ ...etapeData, nom: e.target.value })} required /></FormGroup>
                    <Row>
                        <Col md={6}>
                            <FormGroup><Label>Date début</Label><Input type="date" value={etapeData.date_debut} onChange={(e) => setEtapeData({ ...etapeData, date_debut: e.target.value })} /></FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup><Label>Date fin</Label><Input type="date" value={etapeData.date_fin} onChange={(e) => setEtapeData({ ...etapeData, date_fin: e.target.value })} /></FormGroup></Col>
                    </Row>
                    <FormGroup>
                        <Label>Statut</Label>
                        <Input type="select" value={etapeData.statut} onChange={(e) => setEtapeData({ ...etapeData, statut: e.target.value })}>
                            <option value="non_commence">Non commencé</option>
                            <option value="en_cours">En cours</option>
                            <option value="termine">Terminé</option>
                        </Input>
                    </FormGroup>
                </ModalBody><ModalFooter><Button color="secondary" onClick={toggleEtapeModal} disabled={isSubmittingEtape}>Annuler</Button><Button color="primary" type="submit" disabled={isSubmittingEtape}>{isSubmittingEtape ? <><i className="mdi mdi-loading mdi-spin me-1"></i> Enregistrement...</> : (editingEtape ? "Mettre à jour" : "Enregistrer")}</Button></ModalFooter></Form>
            </Modal>

            <Modal isOpen={ticketModal} toggle={toggleTicketModal} size="lg">
                <ModalHeader toggle={toggleTicketModal}>{editingTicket ? "Modifier le ticket" : "Créer un ticket"}</ModalHeader>
                <Form onSubmit={handleTicketSubmit}><ModalBody>
                    <FormGroup><Label>Titre du Ticket</Label><Input type="text" value={ticketData.titre} onChange={(e) => setTicketData({ ...ticketData, titre: e.target.value })} required /></FormGroup>
                    <FormGroup><Label>Description</Label><Input type="textarea" rows="3" value={ticketData.description} onChange={(e) => setTicketData({ ...ticketData, description: e.target.value })} required /></FormGroup>

                    <FormGroup>
                        <Label>Étape *</Label>
                        <Input type="select" value={ticketData.etape_id} onChange={(e) => setTicketData({ ...ticketData, etape_id: e.target.value })} required>
                            <option value="">Sélectionner une étape</option>
                            {(projet.etapes || []).sort((a, b) => a.ordre - b.ordre).map(e => (
                                <option key={e.id} value={e.id}>{e.ordre}. {e.nom}</option>
                            ))}
                        </Input>
                    </FormGroup>

                    <Row>
                        <Col md={6}><FormGroup><Label>Assigner à</Label><Input type="select" value={ticketData.assigned_to} onChange={(e) => setTicketData({ ...ticketData, assigned_to: e.target.value })}><option value="">Non assigné</option>{users.filter(u => u.role !== 'client').map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}</Input></FormGroup></Col>
                        <Col md={6}><FormGroup><Label>Priorité</Label><Input type="select" value={ticketData.priorite} onChange={(e) => setTicketData({ ...ticketData, priorite: e.target.value })}><option value="basse">Basse</option><option value="normale">Normale</option><option value="haute">Haute</option><option value="urgente">Urgente</option></Input></FormGroup></Col>
                    </Row>

                    <Row>
                        <Col md={4}>
                            <FormGroup><Label>Heures Estimées</Label><Input type="number" step="0.5" value={ticketData.heures_estimees} onChange={(e) => setTicketData({ ...ticketData, heures_estimees: e.target.value })} placeholder="Ex: 4.5" /></FormGroup>
                        </Col>
                        {editingTicket && (
                            <Col md={4}>
                                <FormGroup><Label>Heures Réelles</Label><Input type="number" step="0.5" value={ticketData.heures_reelles} onChange={(e) => setTicketData({ ...ticketData, heures_reelles: e.target.value })} placeholder="Ex: 2" /></FormGroup>
                            </Col>
                        )}
                        <Col md={editingTicket ? 4 : 8}>
                            <FormGroup><Label>Échéance (Deadline)</Label><Input type="date" value={ticketData.deadline} onChange={(e) => setTicketData({ ...ticketData, deadline: e.target.value })} /></FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <FormGroup><Label>Statut</Label>
                                <Input type="select" value={ticketData.statut} onChange={(e) => setTicketData({ ...ticketData, statut: e.target.value })}>
                                    <option value="en_cours">En cours</option>
                                    <option value="en_attente">En attente</option>
                                    <option value="reopen">Réouvrir (Reopen)</option>
                                    <option value="resolu">Résolu</option>
                                    <option value="ferme">Fermé</option>
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>
                </ModalBody><ModalFooter>                    <Button color="secondary" onClick={toggleTicketModal} disabled={isSubmittingTicket}>Annuler</Button><Button color="primary" type="submit" disabled={isSubmittingTicket}>{isSubmittingTicket ? <><i className="mdi mdi-loading mdi-spin me-1"></i> Enregistrement...</> : (editingTicket ? "Mettre à jour" : "Créer le ticket")}</Button></ModalFooter></Form>
            </Modal>

            <AlertModal
                isOpen={alertConfig.isOpen}
                toggle={toggleAlert}
                type={alertConfig.type}
                title={alertConfig.title}
                message={alertConfig.message}
            />
        </React.Fragment>
    )
}

export default connect(null, { setBreadcrumbItems })(withRouter(ProjetDetails))
